import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin } from 'rxjs';
import {
  CategoriaEquipService,
  PropriedadeEquipService,
  PropriedadeOpcaoService,
  TipoEquipService,
} from '@core/services/domain-services';
import { DataSourceRegistryService } from '@core/services/data-source-registry.service';
import { EquipamentosNavComponent } from '../equipamentos-nav/equipamentos-nav.component';
import {
  CategoriaEquipamento,
  DropdownOption,
  EnumMarcaCategoria,
  PropriedadeEquipamento,
  PropriedadeOpcao,
  TipoEquipamento,
} from '@core/models/models';

@Component({
  selector: 'app-tipos-equipamento',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    SkeletonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    EquipamentosNavComponent,
  ],
  templateUrl: './tipos-equipamento.component.html',
  styleUrl: './tipos-equipamento.component.scss',
})
export class TiposEquipamentoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  @Input() embedded = false;
  @Input() presetTipoId: number | null = null;

  private readonly svc = inject(TipoEquipService);
  private readonly categoriaSvc = inject(CategoriaEquipService);
  private readonly propriedadeSvc = inject(PropriedadeEquipService);
  private readonly opcaoSvc = inject(PropriedadeOpcaoService);
  private readonly registry = inject(DataSourceRegistryService);
  private readonly msg = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  tipos: TipoEquipamento[] = [];
  categorias: CategoriaEquipamento[] = [];
  propriedades: PropriedadeEquipamento[] = [];
  opcoes: PropriedadeOpcao[] = [];
  marcas: DropdownOption[] = [];
  modelos: DropdownOption[] = [];

  loading = true;
  loadingPropriedades = false;
  loadingOpcoes = false;
  loadingCatalogo = false;
  saving = false;
  selectedTipoId: number | null = null;
  selectedMarcaId: number | null = null;
  activeSection: 'propriedades' | 'catalogo' = 'propriedades';

  propriedadeDialog = false;
  opcaoDialog = false;
  tipoDialog = false;
  editingPropriedadeId: number | null = null;
  opcaoPropriedade: PropriedadeEquipamento | null = null;
  novaOpcao = '';
  novaMarca = '';
  novoModelo = '';

  readonly tiposDado = [
    { label: 'Texto', value: 1 },
    { label: 'Número', value: 2 },
    { label: 'Booleano', value: 3 },
    { label: 'Data', value: 4 },
    { label: 'Lista', value: 6 },
  ];

  readonly fontes = [
    { label: 'Sem fonte externa', value: null },
    { label: 'Tipos de arma', value: 'TIPOS_ARMA' },
    { label: 'Marcas de arma', value: 'MARCAS_ARMA' },
    { label: 'Marcas de viatura', value: 'MARCAS_VEICULO' },
    { label: 'Marcas de utensílio', value: 'MARCAS_UTENSILIO' },
    { label: 'Modelos por marca', value: 'MODELOS_POR_MARCA' },
    { label: 'Estados de arma', value: 'ESTADOS_ARMA' },
    { label: 'Estados de viatura', value: 'ESTADOS_VEICULO' },
    { label: 'Estados de utensílio', value: 'ESTADOS_UTENSILIO' },
    { label: 'Tipos de combustível', value: 'TIPOS_COMBUSTIVEL' },
    { label: 'Categorias de utensílio', value: 'CATEGORIAS_UTENSILIO' },
    { label: 'Postos', value: 'POSTOS' },
    { label: 'Efetivos', value: 'EFETIVOS' },
    { label: 'Cores', value: 'CORES' },
    { label: 'Países', value: 'PAISES' },
    { label: 'Sim / Não', value: 'SIM_NAO' },
  ];

  readonly propriedadeForm = this.fb.group({
    nome: ['', [Validators.required, Validators.maxLength(120)]],
    tipoDado: [1, Validators.required],
    icon: ['pi pi-list'],
    sourceKey: [null as string | null],
    dependeDe: [null as string | null],
    endpointReferencia: [null as string | null],
    campoLabel: [null as string | null],
    campoValue: [null as string | null],
    cache: [false],
    ativo: [true],
    formato: [null as string | null],
  });

  readonly tipoForm = this.fb.group({
    designacao: ['', [Validators.required, Validators.maxLength(100)]],
    idCategoriaEquipamento: [null as number | null, Validators.required],
    icon: ['pi pi-box'],
  });

  get tipoSelecionado(): TipoEquipamento | undefined {
    return this.tipos.find(tipo => tipo.id === this.selectedTipoId);
  }

  get propriedadesDependencia(): DropdownOption[] {
    return this.propriedades
      .filter(prop => prop.id !== this.editingPropriedadeId)
      .map(prop => ({ label: this.formatarNome(prop.nome), value: prop.nome }));
  }

  get isListaForm(): boolean {
    return Number(this.propriedadeForm.controls.tipoDado.value) === 6;
  }

  ngOnInit(): void {
    this.carregarBase();
    this.propriedadeForm.controls.tipoDado.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tipoDado => {
        if (Number(tipoDado) === 6) {
          this.propriedadeForm.patchValue({
            sourceKey: null,
            dependeDe: null,
            endpointReferencia: 'GET /api/PropriedadeOpcoes',
            campoLabel: null,
            campoValue: null,
          }, { emitEvent: false });
        }
      });
  }

  carregarBase(selectId?: number): void {
    this.loading = true;
    forkJoin({
      tipos: this.svc.getAll({ pageSize: 200 }),
      categorias: this.categoriaSvc.getAll({ pageSize: 200 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tipos, categorias }) => {
          this.tipos = tipos.data ?? [];
          this.categorias = categorias.data ?? [];
          this.loading = false;
          const nextId = this.presetTipoId ?? selectId ??
            (this.selectedTipoId && this.tipos.some(t => t.id === this.selectedTipoId)
              ? this.selectedTipoId
              : this.tipos[0]?.id);
          this.onTipoChange(nextId ?? null);
          this.cdr.detectChanges();
        },
        error: () => {
          this.showError('Falha ao carregar as configurações de equipamento.');
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onTipoChange(tipoId: number | null): void {
    this.selectedTipoId = tipoId;
    this.selectedMarcaId = null;
    this.modelos = [];
    this.propriedades = [];
    if (!tipoId) return;
    this.carregarPropriedades();
    this.carregarCatalogo();
  }

  carregarPropriedades(): void {
    if (!this.selectedTipoId) return;
    this.loadingPropriedades = true;
    this.propriedadeSvc
      .getByTipoEquipamento(this.selectedTipoId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingPropriedades = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: res => {
          this.propriedades = res.data ?? [];
        },
        error: () => this.showError('Não foi possível carregar as propriedades.'),
      });
  }

  abrirNovaPropriedade(): void {
    if (!this.selectedTipoId) return;
    this.editingPropriedadeId = null;
    this.propriedadeForm.reset({
      nome: '',
      tipoDado: 1,
      icon: 'pi pi-list',
      sourceKey: null,
      dependeDe: null,
      endpointReferencia: null,
      campoLabel: null,
      campoValue: null,
      cache: false,
      ativo: true,
      formato: null,
    });
    this.propriedadeDialog = true;
  }

  editarPropriedade(prop: PropriedadeEquipamento): void {
    this.editingPropriedadeId = prop.id;
    this.propriedadeForm.reset({
      nome: prop.nome,
      tipoDado: prop.tipoDado,
      icon: prop.icon ?? 'pi pi-list',
      sourceKey: prop.sourceKey ?? null,
      dependeDe: prop.dependeDe ?? null,
      endpointReferencia: prop.endpointReferencia ?? null,
      campoLabel: prop.campoLabel ?? null,
      campoValue: prop.campoValue ?? null,
      cache: !!prop.cache,
      ativo: prop.ativo !== false,
      formato: prop.formato ?? null,
    });
    this.propriedadeDialog = true;
  }

  guardarPropriedade(): void {
    if (this.propriedadeForm.invalid || !this.selectedTipoId || this.saving) {
      this.propriedadeForm.markAllAsTouched();
      return;
    }

    const raw = this.propriedadeForm.getRawValue();
    const lista = Number(raw.tipoDado) === 6;
    const dto: Partial<PropriedadeEquipamento> = {
      tipoEquipamentoId: this.selectedTipoId,
      nome: this.normalizarNome(raw.nome ?? ''),
      tipoDado: Number(raw.tipoDado),
      ativo: !!raw.ativo,
      icon: raw.icon?.trim() || null,
      sourceKey: lista ? null : raw.sourceKey,
      dependeDe: lista ? null : raw.dependeDe,
      endpointReferencia: lista
        ? 'GET /api/PropriedadeOpcoes'
        : raw.endpointReferencia,
      campoLabel: lista ? null : raw.campoLabel,
      campoValue: lista ? null : raw.campoValue,
      cache: !!raw.cache,
      formato: raw.formato,
    };

    this.saving = true;
    const request = this.editingPropriedadeId
      ? this.propriedadeSvc.update(this.editingPropriedadeId, dto)
      : this.propriedadeSvc.create(dto);
    request
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: () => {
          this.propriedadeDialog = false;
          this.carregarPropriedades();
          this.showSuccess(
            this.editingPropriedadeId
              ? 'Propriedade atualizada.'
              : 'Propriedade adicionada.',
          );
        },
        error: err => this.showError(
          err?.error?.message ?? 'Não foi possível guardar a propriedade.',
        ),
      });
  }

  abrirOpcoes(prop: PropriedadeEquipamento): void {
    this.opcaoPropriedade = prop;
    this.opcoes = [];
    this.novaOpcao = '';
    this.opcaoDialog = true;
    this.carregarOpcoes();
  }

  carregarOpcoes(): void {
    if (!this.opcaoPropriedade) return;
    this.loadingOpcoes = true;
    this.opcaoSvc
      .getByPropriedade(this.opcaoPropriedade.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingOpcoes = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: res => {
          this.opcoes = (res.data ?? [])
            .filter(item => item.propriedadeId === this.opcaoPropriedade?.id);
        },
        error: () => this.showError('Não foi possível carregar as opções.'),
      });
  }

  adicionarOpcao(): void {
    const valor = this.novaOpcao.trim();
    if (!valor || !this.opcaoPropriedade || this.saving) return;
    if (this.opcoes.some(item =>
      item.valor.localeCompare(valor, undefined, { sensitivity: 'base' }) === 0
    )) {
      this.showError('Esta opção já existe nesta propriedade.');
      return;
    }
    this.saving = true;
    this.opcaoSvc
      .create({ propriedadeId: this.opcaoPropriedade.id, valor })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: () => {
          this.novaOpcao = '';
          this.carregarOpcoes();
          this.showSuccess('Opção adicionada.');
        },
        error: err => this.showError(
          err?.error?.message ?? 'Não foi possível adicionar a opção.',
        ),
      });
  }

  carregarCatalogo(): void {
    const sourceKey = this.marcaSourceKey;
    const categoria = this.marcaCategoria;
    this.marcas = [];
    if (!sourceKey || categoria == null) return;
    this.loadingCatalogo = true;
    this.registry
      .resolve(sourceKey, categoria, false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingCatalogo = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: options => {
          this.marcas = options;
        },
        error: () => this.showError('Não foi possível carregar as marcas.'),
      });
  }

  onMarcaChange(marcaId: number | null): void {
    this.selectedMarcaId = marcaId;
    this.modelos = [];
    if (!marcaId) return;
    this.loadingCatalogo = true;
    this.registry
      .resolve('MODELOS_POR_MARCA', marcaId, false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingCatalogo = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: options => {
          this.modelos = options;
        },
        error: () => this.showError('Não foi possível carregar os modelos.'),
      });
  }

  adicionarMarca(): void {
    const nome = this.novaMarca.trim();
    const sourceKey = this.marcaSourceKey;
    const categoria = this.marcaCategoria;
    if (!nome || !sourceKey || categoria == null || this.saving) return;
    this.saving = true;
    this.registry
      .createOption(sourceKey, categoria, nome)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: options => {
          this.marcas = options;
          const criada = options.find(o =>
            o.label.localeCompare(nome, undefined, { sensitivity: 'base' }) === 0
          );
          this.novaMarca = '';
          if (criada) this.onMarcaChange(Number(criada.value));
          this.showSuccess('Marca adicionada.');
        },
        error: err => this.showError(
          err?.error?.message ?? 'Não foi possível adicionar a marca.',
        ),
      });
  }

  adicionarModelo(): void {
    const nome = this.novoModelo.trim();
    if (!nome || !this.selectedMarcaId || this.saving) return;
    this.saving = true;
    this.registry
      .createOption('MODELOS_POR_MARCA', this.selectedMarcaId, nome)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: options => {
          this.modelos = options;
          this.novoModelo = '';
          this.showSuccess('Modelo adicionado.');
        },
        error: err => this.showError(
          err?.error?.message ?? 'Não foi possível adicionar o modelo.',
        ),
      });
  }

  abrirNovoTipo(): void {
    this.tipoForm.reset({
      designacao: '',
      idCategoriaEquipamento: null,
      icon: 'pi pi-box',
    });
    this.tipoDialog = true;
  }

  guardarTipo(): void {
    if (this.tipoForm.invalid || this.saving) {
      this.tipoForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    const raw = this.tipoForm.getRawValue();
    this.svc
      .create({
        designacao: raw.designacao?.trim() ?? '',
        idCategoriaEquipamento: raw.idCategoriaEquipamento ?? undefined,
        icon: raw.icon?.trim() || 'pi pi-box',
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: tipo => {
          this.tipoDialog = false;
          this.carregarBase(this.presetTipoId ?? tipo.id);
          this.showSuccess('Tipo de equipamento adicionado.');
        },
        error: err => this.showError(
          err?.error?.message ?? 'Não foi possível adicionar o tipo.',
        ),
      });
  }

  isLista(prop: PropriedadeEquipamento): boolean {
    return Number(prop.tipoDado) === 6 ||
      (prop.tipoDadoDescricao ?? '').toLocaleLowerCase() === 'lista';
  }

  formatarNome(nome: string): string {
    return nome
      .toLocaleLowerCase()
      .split('_')
      .filter(Boolean)
      .map(part => part.charAt(0).toLocaleUpperCase() + part.slice(1))
      .join(' ');
  }

  private normalizarNome(nome: string): string {
    return nome
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();
  }

  private get marcaCategoria(): EnumMarcaCategoria | null {
    const nome = this.normalizarNome(this.tipoSelecionado?.designacao ?? '');
    if (/VIATUR|VEICUL/.test(nome)) return EnumMarcaCategoria.VEICULO;
    if (/ARMA/.test(nome)) return EnumMarcaCategoria.ARMA;
    if (/UTENSIL/.test(nome)) return EnumMarcaCategoria.UTENSILIO;
    return null;
  }

  private get marcaSourceKey(): string | null {
    const categoria = this.marcaCategoria;
    if (categoria === EnumMarcaCategoria.VEICULO) return 'MARCAS_VEICULO';
    if (categoria === EnumMarcaCategoria.ARMA) return 'MARCAS_ARMA';
    if (categoria === EnumMarcaCategoria.UTENSILIO) return 'MARCAS_UTENSILIO';
    return null;
  }

  private showSuccess(detail: string): void {
    this.msg.add({ severity: 'success', summary: 'Sucesso', detail, life: 3000 });
  }

  private showError(detail: string): void {
    this.msg.add({ severity: 'error', summary: 'Erro', detail, life: 4000 });
  }
}
